import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const EASE = [0.21, 0.47, 0.32, 0.98];

const FAQS = [
  { q: 'Is it safe for my Instagram accounts?', a: 'Yes — we use human-like typing, randomized delays, and daily send caps to stay well within safe limits and protect your accounts.' },
  { q: 'Do I need to keep my computer on?', a: 'Sending runs through a Chrome extension during your set window. We schedule and pace everything; you just keep the extension active.' },
  { q: 'Is there a free trial?', a: 'Every plan starts with a 3-day free trial — no credit card required to begin.' },
  { q: 'Can I change plans anytime?', a: 'Absolutely. Upgrade, downgrade, or cancel whenever you want from your billing portal.' },
];

const ClosingSection = ({ darkMode }) => {
  return (
    <section className="py-16 lg:py-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* FAQ */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5, ease: EASE }}
          className={`text-3xl font-extrabold tracking-tight text-center mb-10 ${darkMode ? 'text-white' : 'text-gray-900'}`}
        >
          Frequently asked questions
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-16">
          {FAQS.map((f, i) => (
            <motion.div
              key={f.q}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.4, delay: i * 0.12, ease: EASE }}
              className={`p-6 rounded-xl border text-left ${darkMode ? 'bg-slate-800/70 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}
            >
              <h3 className={`font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{f.q}</h3>
              <p className={`text-sm leading-relaxed ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>{f.a}</p>
            </motion.div>
          ))}
        </div>

        {/* Final CTA band */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, ease: EASE }}
          className={`relative isolate overflow-hidden rounded-3xl px-6 py-14 text-center ${darkMode ? 'bg-gradient-to-br from-cyan-600 to-blue-700' : 'bg-gradient-to-br from-blue-500 to-purple-600'}`}
        >
          <motion.div
            aria-hidden="true"
            initial={{ opacity: 0.25, scale: 0.9 }}
            animate={{ opacity: [0.25, 0.45, 0.25], scale: [0.9, 1.1, 0.9] }}
            transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
            className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -z-10 w-[600px] max-w-[120%] h-64 rounded-full blur-3xl bg-white/20"
          />
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white mb-3">
            Turn cold DMs into warm leads
          </h2>
          <p className="text-white/85 text-lg max-w-xl mx-auto mb-8">
            Start your 3-day free trial and send your first campaign today.
          </p>
          <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} className="inline-block">
            <Link
              to="/signup?trial=true"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-gray-900 font-bold text-base shadow-xl hover:shadow-2xl transition-shadow"
            >
              Start free trial <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
          <p className="text-white/70 text-sm mt-4">No credit card required · Cancel anytime</p>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className={`mt-16 border-t ${darkMode ? 'border-slate-800' : 'border-gray-200'}`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className={`text-sm font-semibold ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>ICEDMS</span>
          <div className={`flex items-center gap-5 text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
            <Link to="/pricing" className="hover:underline">Pricing</Link>
            <Link to="/login" className="hover:underline">Log in</Link>
            <Link to="/signup?trial=true" className="hover:underline">Get started</Link>
          </div>
          <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>© 2026 Ivy Labs</span>
        </div>
      </footer>
    </section>
  );
};

export default ClosingSection;
